import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Grid,
  makeStyles,
  withStyles,
  Paper,
  Theme,
  IconButton,
  Typography,
  Hidden,
  Tooltip,
  Button,
  Divider,
} from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import CategoryVO from 'commons/vo/CategoryVO';
import { useAsset, useInit, useLibrary } from 'hooks';
import StorageVO from 'commons/vo/StorageVO';
import defaultThumbnail from 'asset/thumbnail/thumb_default.jpg';
import WkblVideoVO from 'commons/vo/WkblVideoVO';
import { SpecialProps } from 'components/SpecialListView/type';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers/type';
import asset from 'redux/reducers/asset';
import { File, Resolution } from 'commons/constants/KeyFlowConstants';
import CodeVO from 'commons/vo/CodeVO';
import GetAppIcon from '@material-ui/icons/GetApp';
import SwitchVideoIcon from '@material-ui/icons/SwitchVideo';
import TimeCode from 'commons/utils/TimeCode';
import LibraryParamVO from 'commons/vo/param/LibraryParamVO';

import { VersusIcon, VersusMobileIcon } from 'asset/images';
import { getDayOfWeek } from 'commons/utils/DateUtils';
import DownloadAssetSplitButton from 'components/DownloadAssetSplitButton';

const useStyles = makeStyles((theme: Theme) => ({
  assetItem: {
    // 기존의 계산식 85px - 16px(spacing 2) = 69px
    // 기존의 계산식이 어떤걸 의미하는지는 모르겠으나 변경해도 문제 없는것 같아 요청사항 대로 변경
    marginBottom: '35px',
    [theme.breakpoints.down('md')]: {
      marginBottom: '35px',
    },
  },
  paper: {
    height: '307px',
    boxShadow: theme.shadows[0],
    [theme.breakpoints.down('md')]: {
      height: '232px',
    },
  },
  imgContainer: {
    // [Skim 의 스크러빙 로직 때문에 높이를 지정하면 안됨]
    // - skim 이미지 비율 16:9 이미지가 50장이 세로로 이어 붙임
    // - 때문에 width의 56.25% 의 height 를 가져가야 함
    position: 'relative',
    backgroundOrigin: 'content-box',
    backgroundSize: 'cover',
    paddingTop: '56.25%',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      // 모바일에서 skim 이 동작하지 않는다면 영역 크기 문제로 height 를 지정해주거나 최대 너비등을 지정해야함.
      minWidth: '209px',
      minHeight: '185px',
      paddingTop: 0,
      backgroundPosition: 'center !important',
    },
  },
  img: {
    width: '100%',
  },
  scrubbingBar: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: '100%',
    border: '0.8px solid rgba(255, 30, 1, 0.8)',
  },
  assetContainer: {
    height: '62px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  assetContentContainer: {
    width: '290px',
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      minWidth: 0,
      flexGrow: 1,
      alignSelf: 'auto',
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  assetTitleContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    fontSize: '16px',
    color: '#000',
    marginTop: '10px',

    [theme.breakpoints.down('md')]: {
      width: 'auto',
      flex: '1 1 100%',
      paddingRight: '5px',
      fontSize: '14px',
    },
  },
  assetDateContainer: {
    width: '100%',
    fontSize: '14px',
    color: '#999',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      flex: '1 1 100%',
      paddingRight: '5px',
      fontSize: '11px',
    },
  },
  menuButton: {
    width: '100px',
    height: '36px',
    border: '1px solid #b6b6b6',
    borderRadius: 0,
    backgroundColor: 'transparent',
    fontSize: '14px',
    [theme.breakpoints.down('md')]: {
      flexBasis: '60px',
      flexShrink: 0,
      flexGrow: 0,
      fontSize: '14px',
    },
  },
  typeContainer: {
    width: '112px',
    height: '27px',
    position: 'absolute',
    padding: '2px',
    zIndex: 100,
    top: 0,
    right: 0,
    fontSize: '14px',
    textAlign: 'center',
    color: '#FFF',
    backgroundColor: '#D94F98',
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      height: '20px',
      padding: '0 10px',
      fontSize: '12px',
    },
  },
  centerLine: {
    border: '1px solid gray',
    position: 'absolute',
    left: '50%',
    width: 0,
    height: '100%',
    display: 'inline-block',
  },
  teamContainer: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
  },
  teamLogoContainer: {
    width: '220px',
    height: '110px',
    position: 'relative',
    marginTop: '54px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 1.0)',
    [theme.breakpoints.down('md')]: {
      width: '171px',
      height: '85px',
      marginTop: '40px',
    },
  },
  teamBorder: {
    backgroundColor: '#000',
  },
  team: {
    position: 'relative',
    width: '50%',
    padding: '20px',
    '& > img': {
      width: '100%',
      height: '100%',
    },
  },
  leftBorder: {
    borderLeft: '0.5px solid gray',
  },
  verseTextContainer: {
    position: 'absolute',
    zIndex: 1,
    left: 'calc(50% - 15px)',
    width: '30px',
    lineHeight: 2.0,
    borderRadius: '50%',
    backgroundColor: 'rgba(202, 90, 150, 1.0)',
    color: 'rgba(255, 255, 255, 1.0)',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  teamNameContainer: {
    width: '220px',
    height: '27px',
    display: 'flex',
    margin: 'auto',
    justifyContent: 'space-around',
    backgroundColor: '#999',
    '& > span': {
      padding: '2px',
      flexBasis: '50%',
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 1.0)',
      textAlign: 'center',
      overflow: 'hidden',
    },
    [theme.breakpoints.down('md')]: {
      width: '171px',
      height: '20px',
      '& > span': {
        padding: 0,
        flexBasis: '50%',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 1.0)',
        textAlign: 'center',
        overflow: 'hidden',
      },
    },
  },
}));

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function Special({ specialVideo }: SpecialProps) {
  const classes = useStyles();
  const { onRequestAssetDownload, onRequestAssetDownloadCloudfront } = useAsset();
  const { onInsertLibrary } = useLibrary();

  const { categoryList } = useSelector((state: RootState) => state.category);
  const { storageList } = useSelector((state: RootState) => state.storage);
  const { wkblGameTypeList: gameTypeList, kfScriptRefRangeList: rangeCodeList } = useSelector(
    (state: RootState) => state.code,
  );

  const [skimPoisitionPercent, setSkimPositionPercent] = useState<number>(100);
  const [scrubbingPercent, setScrubbingPercent] = useState<number>(0);
  const [isShowScrubbingBar, setIsShowScrubbingBar] = useState<boolean>(false);
  const [menuButton, setMenuButton] = React.useState<null | HTMLButtonElement>(null);
  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(specialVideo.mediaFps);
    timecodeUtil.setFrames(specialVideo.inPoint);
    return timecodeUtil;
  });
  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(specialVideo.mediaFps);
    timecodeUtil.setFrames(specialVideo.outPoint);
    return timecodeUtil;
  });
  const [durationTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(specialVideo.mediaFps);
    timecodeUtil.setFrames(specialVideo.outPoint - specialVideo.inPoint);
    return timecodeUtil;
  });

  const storage: StorageVO | null = useMemo(() => {
    let result = null;
    const category = categoryList.filter(({ id }: CategoryVO) => id === specialVideo.categoryId)[0];
    if (category) {
      result = storageList.filter(({ id }: StorageVO) => id === category.storageId)[0];
    }
    return result;
  }, [categoryList, storageList, specialVideo.categoryId]);

  const symbolicPath: string = useMemo(() => {
    if (storage) {
      return storage.symbolicPath;
    }
    return '/Storage1';
  }, [storage]);

  const thumbnailPath = useMemo(() => {
    return specialVideo.getThumbnailFilePath(symbolicPath);
  }, [symbolicPath]);

  const skimPath = useMemo(() => {
    return specialVideo.getSkimFilePath(symbolicPath);
  }, [symbolicPath]);

  const team1LogoPath = useMemo(() => {
    return specialVideo.getTeam1LogoPath(symbolicPath);
  }, [asset, symbolicPath]);

  const team2LogoPath = useMemo(() => {
    return specialVideo.getTeam2LogoPath(symbolicPath);
  }, [asset, symbolicPath]);

  const teamName = useMemo(() => {
    const { team1Name, team2Name } = specialVideo;
    const team1 = team1Name ? team1Name.split(' ')[0] : '';
    const team2 = team2Name ? team2Name.split(' ')[0] : '';

    return [team1, team2];
  }, [specialVideo.team1Name, specialVideo.team2Name]);

  const gameTypeName = useMemo(() => {
    const gameTypeItem: CodeVO[] =
      gameTypeList.filter((item: CodeVO) => item.id === Number(specialVideo.gameType)) || [];
    if (gameTypeItem.length > 0) {
      return gameTypeItem[0].name || '없음';
    }
    return '없음';
  }, []);

  const backgroundSkimImageStyle = useMemo(
    () => specialVideo.getBackgroundImage(isShowScrubbingBar),
    [isShowScrubbingBar, specialVideo],
  );

  const backgroundSkimPosition = useMemo(() => {
    return isShowScrubbingBar
      ? `0 ${skimPoisitionPercent}%, left bottom, left bottom`
      : 'left bottom, left bottom';
  }, [isShowScrubbingBar, skimPoisitionPercent]);

  const rangeCodeName = useMemo(() => {
    const rangeCode: CodeVO[] =
      rangeCodeList.filter((item: CodeVO) => item.codevalue === String(specialVideo.subType)) || [];
    if (rangeCode.length > 0) {
      return rangeCode[0].name || '';
    }
    return '';
  }, [rangeCodeList, specialVideo]);

  const isShowTeamContainer = useMemo(() => {
    return specialVideo.team1 && specialVideo.team2;
  }, [specialVideo]);

  const { team1, team2 } = useMemo(() => {
    return specialVideo.getTeamLogoPath();
  }, [specialVideo]);

  const onMouseMove = useCallback(event => {
    const mouseX = event.nativeEvent.x - event.currentTarget.getBoundingClientRect().left;

    const imageWidth = event.currentTarget.offsetWidth;
    const mousePercent: number = (mouseX / imageWidth) * 100.0;
    setScrubbingPercent(mousePercent);

    const integerPercent: number = parseInt(mousePercent.toFixed(0));

    if (integerPercent % 2 === 0) {
      setSkimPositionPercent(100 - integerPercent);
    }
  }, []);

  const onMouseEnterThumbnail = useCallback(() => {
    setIsShowScrubbingBar(true);
  }, []);

  const onMouseLeaveThumbnail = useCallback(() => {
    setIsShowScrubbingBar(false);
    setSkimPositionPercent(100);
  }, []);

  const onPopupAssetDetail = useCallback(
    (event: React.MouseEvent) => {
      const { refId, id, title, subType } = specialVideo;
      const inTime = inTimecodeUtil.getSeconds();
      const outTime = outTimecodeUtil.getSeconds();
      const encodedKeyword = encodeURIComponent(subType);
      let redirectUrl = `/preview/${id}`;
      if (refId !== id && rangeCodeName) {
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}&subType=${encodedKeyword}`;
      } else if (refId !== id && !rangeCodeName) {
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}`;
      }
      window.open(redirectUrl);
    },
    [specialVideo, inTimecodeUtil, outTimecodeUtil, rangeCodeName],
  );

  const onDownloadLowres = useCallback(
    (event: React.MouseEvent) => {
      const { id } = specialVideo;
      onRequestAssetDownload({
        videoId: id,
        storageId: storage ? storage.id : 0,
        type: File.LOWRES,
      });
    },
    [specialVideo, storage, onRequestAssetDownload],
  );

  const onDownloadLowresCloudFront = useCallback(() => {
    const { categoryId, lowresPath, title, formattedGameDate } = specialVideo;
    const gameDate = formattedGameDate.replace(/-/g, '');
    onRequestAssetDownloadCloudfront({
      categoryId,
      fileName: lowresPath,
      downloadFileName: `${gameDate}_${title}.mp4`,
    });
  }, []);

  const onRequestLibrary = useCallback(
    (event: React.MouseEvent) => {
      const { categoryId, title, inPoint, outPoint, refId, lowresPath } = specialVideo;
      // const param = new LibraryParamVO({
      //   contents: title,
      //   inPoint,
      //   outPoint,
      //   resolution: Resolution.LOWRES,
      //   categoryId,
      //   videoId: refId,
      // });
      const param = new LibraryParamVO({
        contents: title,
        inPoint,
        outPoint,
        resolution: Resolution.LOWRES,
        categoryId,
        videoId: refId,
        assetTitle: specialVideo.title,
        startTimecode: inTimecodeUtil.getTimeCodeWithFrame(),
        endTimecode: outTimecodeUtil.getTimeCodeWithFrame(),
        duration: durationTimecodeUtil.getSeconds(),
        // videoFilePath: 's3://dummy-tr-src/1637209238569_232.mp4',
        isClean: false,
      });
      onInsertLibrary(param);
    },
    [specialVideo, storage, onInsertLibrary],
  );

  const onOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuButton(event.currentTarget);
  }, []);

  const onCloseMenu = useCallback(() => {
    setMenuButton(null);
  }, []);

  return (
    <Grid item xs={12} lg={4} key={specialVideo.id} className={classes.assetItem}>
      <Paper elevation={2} className={clsx(classes.paper)}>
        <div
          className={clsx(classes.imgContainer)}
          style={{
            backgroundImage: backgroundSkimImageStyle,
            backgroundPosition: backgroundSkimPosition,
          }}
          onMouseMove={onMouseMove}
          onMouseEnter={onMouseEnterThumbnail}
          onMouseLeave={onMouseLeaveThumbnail}
          onClick={onPopupAssetDetail}
        >
          <div
            className={clsx(classes.scrubbingBar)}
            style={{
              left: `${scrubbingPercent}%`,
              display: isShowScrubbingBar ? 'inline-block' : 'none',
            }}
          />
          {isShowTeamContainer && (
            <div
              className={classes.teamContainer}
              style={{
                display: !isShowScrubbingBar ? 'inline-block' : 'none',
              }}
            >
              <div className={classes.teamLogoContainer}>
                {/* 홈 팀 */}
                <span className={clsx(classes.team)}>
                  <img src={team1.db}></img>
                </span>
                <Divider className={classes.teamBorder} orientation="vertical" />
                <img className={classes.verseTextContainer} src={VersusIcon} />
                {/* 어웨이 팀 */}
                <span className={classes.team}>
                  <img src={team2.db}></img>
                </span>
              </div>
              <div className={classes.teamNameContainer}>
                {/* 홈 팀 */}
                <span style={{ backgroundColor: specialVideo.team1Color }}>{teamName[0]}</span>
                <Divider className={classes.teamBorder} orientation="vertical" />
                {/* 어웨이 팀 */}
                <span style={{ backgroundColor: specialVideo.team2Color }}>{teamName[1]}</span>
              </div>
            </div>
          )}
          {rangeCodeName && (
            <Typography className={classes.typeContainer}>{rangeCodeName}</Typography>
          )}
        </div>
        <div className={clsx(classes.assetContainer)}>
          <div className={classes.assetContentContainer}>
            <HtmlTooltip
              title={
                <React.Fragment>
                  <Typography>{specialVideo.title}</Typography>
                </React.Fragment>
              }
            >
              <div className={clsx(classes.assetTitleContainer)}>{specialVideo.title}</div>
            </HtmlTooltip>
            <div className={clsx(classes.assetDateContainer)}>
              {specialVideo.formattedGameDate} ({getDayOfWeek(specialVideo.formattedGameDate)})
            </div>
          </div>

          <Hidden lgUp>
            {specialVideo.isVideo ? (
              <Tooltip title="다운로드">
                <DownloadAssetSplitButton
                  asset={specialVideo}
                  onDownloadClick={onDownloadLowresCloudFront}
                />
              </Tooltip>
            ) : (
              <Tooltip title="부분인출">
                <IconButton className={clsx(classes.menuButton)} onClick={onRequestLibrary}>
                  <SwitchVideoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Hidden>
          <Hidden mdDown>
            {specialVideo.isVideo ? (
              <DownloadAssetSplitButton
                asset={specialVideo}
                onDownloadClick={onDownloadLowresCloudFront}
              />
            ) : (
              <Button
                className={clsx(classes.menuButton)}
                startIcon={<SwitchVideoIcon fontSize="small" />}
                onClick={onRequestLibrary}
              >
                부분인출
              </Button>
            )}
          </Hidden>

          {/* <Hidden lgUp>
            {specialVideo.isVideo ? (
              <Tooltip title="다운로드">
                <IconButton className={clsx(classes.menuButton)} onClick={onDownloadLowres}>
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="부분인출">
                <IconButton className={clsx(classes.menuButton)} onClick={onRequestLibrary}>
                  <SwitchVideoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Hidden>
          <Hidden mdDown>
            {specialVideo.isVideo ? (
              <Button
                className={clsx(classes.menuButton)}
                startIcon={<GetAppIcon fontSize="small" />}
                onClick={onDownloadLowres}
              >
                다운로드
              </Button>
            ) : (
              <Button
                className={clsx(classes.menuButton)}
                startIcon={<SwitchVideoIcon fontSize="small" />}
                onClick={onRequestLibrary}
              >
                부분인출
              </Button>
            )}
          </Hidden> */}
        </div>
      </Paper>
    </Grid>
  );
}

export default Special;
